import React, { useEffect, useMemo, useState } from 'react';
import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../../shared/constants/theme';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Slider,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useTheme } from '@mui/material/styles';
import moment from 'moment';
import { DataTable } from '../../shared';
import {
  createStockRawMaterial,
  deleteStockRawMaterial,
  getStockRawMaterials,
  updateStockRawMaterial,
} from '../../services/api';
import {
  getStockListHeaderSubtitleSx,
  getStockListHeaderTitleSx,
  getStockListPaperSx,
} from '../../utils/stockMobileLayout';
import {
  getDrawerRootSx,
  getDrawerSelectMenuProps,
} from '../../utils/drawerOverlayProps';

const UNIT_OPTIONS = [
  'kg',
  'g',
  'mg',
  'quintal',
  'ton',
  'lb',
  'oz',
  'ltr',
  'ml',
  'cl',
  'gallon',
  'pcs',
  'dozen',
  'pair',
  'packet',
  'box',
  'bag',
  'bottle',
  'can',
  'jar',
  'tin',
  'tub',
  'tray',
  'carton',
  'sachet',
  'pouch',
  'roll',
  'bundle',
  'sheet',
  'stick',
  'cup',
  'tbsp',
  'tsp',
];

const DEFAULT_MIN_STOCK_PCT = 15;
const DEFAULT_REORDER_PCT = 25;

const normalizePercentValue = (value, fallback) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return fallback;
  if (n <= 100) return Math.round(n);
  return fallback;
};

const formatPercentLabel = (value) => `${normalizePercentValue(value, 0)}%`;

const StockPercentSlider = ({ label, value, onChange, disabled, helperText, isDark }) => {
  const pct = normalizePercentValue(value, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? '#e5e7eb' : '#111827' }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#4ce1b6', minWidth: 40, textAlign: 'right' }}>
          {pct}%
        </Typography>
      </Box>
      <Slider
        value={pct}
        min={0}
        max={100}
        step={1}
        onChange={(_, next) => onChange(next)}
        disabled={disabled}
        sx={{
          color: '#4ce1b6',
          height: 6,
          '& .MuiSlider-rail': {
            opacity: isDark ? 0.28 : 0.2,
            backgroundColor: isDark ? '#64748b' : '#94a3b8',
          },
          '& .MuiSlider-track': {
            border: 'none',
          },
          '& .MuiSlider-thumb': {
            width: 16,
            height: 16,
            backgroundColor: '#4ce1b6',
            boxShadow: '0 2px 6px rgba(76, 225, 182, 0.45)',
            '&:hover, &.Mui-focusVisible': {
              boxShadow: '0 0 0 8px rgba(76, 225, 182, 0.16)',
            },
          },
        }}
      />
      {helperText ? (
        <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', display: 'block', mt: 0.5 }}>
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
};

const getInitialFormData = () => ({
  name: '',
  category: 'other',
  sku: '',
  unit: 'kg',
  minimum_stock: DEFAULT_MIN_STOCK_PCT,
  reorder_quantity: DEFAULT_REORDER_PCT,
  is_active: true,
});

const StockRawMaterialSection = ({ isDark }) => {
  const theme = useTheme();
  const drawerSelectMenuProps = useMemo(
    () => getDrawerSelectMenuProps(isDark, isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff'),
    [isDark]
  );
  const [rawMaterials, setRawMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('add');
  const [selectedRawMaterialId, setSelectedRawMaterialId] = useState(null);
  const [formMeta, setFormMeta] = useState({ is_system_default: false });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rawMaterialToDelete, setRawMaterialToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formData, setFormData] = useState(getInitialFormData());
  const [saveLoading, setSaveLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });
  const [pagination, setPagination] = useState({
    page: 0,
    perPage: 10,
    totalRecords: 0,
  });
  const [sortConfig, setSortConfig] = useState({ column: 'updated_at', direction: 'desc' });
  const [filters, setFilters] = useState({
    search: '',
    unit: '',
    status: '',
  });

  const isReadOnly = drawerMode === 'view';
  const isFormValid = Boolean(String(formData.name || '').trim()) && Boolean(formData.unit);

  const showMessage = (severity, message) => {
    setSnackbar({ open: true, severity, message });
  };

  const loadRawMaterials = async () => {
    setLoading(true);
    try {
      const response = await getStockRawMaterials();
      setRawMaterials(response?.raw_materials || []);
    } catch (error) {
      showMessage('error', error?.userMessage || 'Failed to load raw materials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRawMaterials();
  }, []);

  const filteredAndSortedRawMaterials = useMemo(() => {
    const normalizedSearch = String(filters.search || '').trim().toLowerCase();
    const filtered = rawMaterials.filter((rawMaterial) => {
      const matchesUnit = !filters.unit || (rawMaterial.unit || '') === filters.unit;
      const currentStatus = rawMaterial.is_active ? 'active' : 'inactive';
      const matchesStatus = !filters.status || currentStatus === filters.status;
      const searchable = [rawMaterial.name, rawMaterial.unit].filter(Boolean).join(' ').toLowerCase();
      const matchesSearch = !normalizedSearch || searchable.includes(normalizedSearch);
      return matchesUnit && matchesStatus && matchesSearch;
    });

    filtered.sort((a, b) => {
      const field = sortConfig.column;
      if (field === 'updated_at') {
        const ta = a.updated_at ? moment(a.updated_at).valueOf() : 0;
        const tb = b.updated_at ? moment(b.updated_at).valueOf() : 0;
        const cmp = ta - tb;
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      }
      const aValue = String(a[field] ?? '').toLowerCase();
      const bValue = String(b[field] ?? '').toLowerCase();
      if (sortConfig.direction === 'asc') return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    });
    return filtered;
  }, [rawMaterials, filters, sortConfig]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, totalRecords: filteredAndSortedRawMaterials.length }));
  }, [filteredAndSortedRawMaterials.length]);

  const paginatedData = useMemo(() => {
    const start = pagination.page * pagination.perPage;
    const end = start + pagination.perPage;
    return filteredAndSortedRawMaterials.slice(start, end);
  }, [filteredAndSortedRawMaterials, pagination.page, pagination.perPage]);

  const openAddDrawer = () => {
    setDrawerMode('add');
    setSelectedRawMaterialId(null);
    setFormMeta({ is_system_default: false });
    setFormData(getInitialFormData());
    setDrawerOpen(true);
  };

  const openEditDrawer = (rawMaterial) => {
    setDrawerMode('edit');
    setSelectedRawMaterialId(rawMaterial.id);
    setFormMeta({ is_system_default: Boolean(rawMaterial.is_system_default) });
    setFormData({
      name: rawMaterial.name || '',
      category: rawMaterial.category || 'other',
      sku: rawMaterial.sku || '',
      unit: rawMaterial.unit || 'kg',
      minimum_stock: normalizePercentValue(rawMaterial.minimum_stock, DEFAULT_MIN_STOCK_PCT),
      reorder_quantity: normalizePercentValue(rawMaterial.reorder_quantity, DEFAULT_REORDER_PCT),
      is_active: rawMaterial.is_active ?? true,
    });
    setDrawerOpen(true);
  };

  const openViewDrawer = (rawMaterial) => {
    setDrawerMode('view');
    setSelectedRawMaterialId(rawMaterial.id);
    setFormMeta({ is_system_default: Boolean(rawMaterial.is_system_default) });
    setFormData({
      name: rawMaterial.name || '',
      category: rawMaterial.category || 'other',
      sku: rawMaterial.sku || '',
      unit: rawMaterial.unit || 'kg',
      minimum_stock: normalizePercentValue(rawMaterial.minimum_stock, DEFAULT_MIN_STOCK_PCT),
      reorder_quantity: normalizePercentValue(rawMaterial.reorder_quantity, DEFAULT_REORDER_PCT),
      is_active: rawMaterial.is_active ?? true,
    });
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    if (isReadOnly || !isFormValid || formMeta.is_system_default) return;
    setSaveLoading(true);
    const payload = {
      name: String(formData.name || '').trim(),
      category: formData.category || 'other',
      sku: String(formData.sku || '').trim() || null,
      unit: formData.unit || 'kg',
      minimum_stock: normalizePercentValue(formData.minimum_stock, DEFAULT_MIN_STOCK_PCT),
      reorder_quantity: normalizePercentValue(formData.reorder_quantity, DEFAULT_REORDER_PCT),
      is_active: Boolean(formData.is_active),
    };
    try {
      if (drawerMode === 'edit' && selectedRawMaterialId) {
        await updateStockRawMaterial(selectedRawMaterialId, payload);
        showMessage('success', 'Raw material updated successfully');
      } else {
        await createStockRawMaterial(payload);
        showMessage('success', 'Raw material created successfully');
      }
      setDrawerOpen(false);
      await loadRawMaterials();
    } catch (error) {
      showMessage('error', error?.userMessage || 'Failed to save raw material');
    } finally {
      setSaveLoading(false);
    }
  };

  const openDeleteDialog = (rawMaterial) => {
    setRawMaterialToDelete({ id: rawMaterial.id, name: rawMaterial.name || '' });
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setRawMaterialToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!rawMaterialToDelete?.id) return;
    setDeleteLoading(true);
    try {
      await deleteStockRawMaterial(rawMaterialToDelete.id);
      showMessage('success', 'Raw material deleted successfully');
      setDeleteDialogOpen(false);
      setRawMaterialToDelete(null);
      await loadRawMaterials();
    } catch (error) {
      showMessage('error', error?.userMessage || 'Failed to delete raw material');
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      field: 'name',
      label: 'Product / Item',
      sortable: true,
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <span>{value || '—'}</span>
          {row.is_system_default && (
            <Chip
              size="small"
              label="DEFAULT"
              sx={{
                height: 22,
                fontSize: '0.65rem',
                fontWeight: 700,
                backgroundColor: alpha('#4ce1b6', 0.16),
                color: '#4ce1b6',
              }}
            />
          )}
        </Box>
      ),
    },
    { field: 'category_label', label: 'Category', sortable: true, render: (value, row) => value || row.category || '—' },
    { field: 'sku', label: 'SKU', sortable: true, render: (value) => value || '—' },
    { field: 'unit', label: 'Unit', sortable: true, render: (value) => (value || '—').toUpperCase() },
    { field: 'minimum_stock', label: 'Min Stock', sortable: true, render: (value) => formatPercentLabel(value) },
    { field: 'reorder_quantity', label: 'Reorder', sortable: true, render: (value) => formatPercentLabel(value) },
    { field: 'current_stock', label: 'Current', sortable: false, render: (value) => value ?? '—' },
    { field: 'last_purchase_price', label: 'Last Price', sortable: true, render: (value) => (value ? `₹${value}` : '—') },
    {
      field: 'updated_at',
      label: 'Last Updated',
      sortable: true,
      render: (value) => (value ? moment(value).format('DD MMM YYYY, hh:mm A') : '—'),
    },
    {
      field: 'status',
      label: 'Status',
      sortable: false,
      render: (value, row) => (
        <Chip
          size="small"
          label={row.is_active ? 'ACTIVE' : 'INACTIVE'}
          sx={{
            backgroundColor: alpha(row.is_active ? '#4ce1b6' : '#9e9e9e', 0.14),
            color: row.is_active ? '#4ce1b6' : (isDark ? '#d1d5db' : '#6b7280'),
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      field: 'actions',
      label: 'Actions',
      sortable: false,
      align: 'right',
      render: (value, row) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <IconButton size="small" onClick={() => openViewDrawer(row)} sx={{ color: theme.palette.text.secondary }}>
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
          {!row.is_system_default && (
            <>
              <IconButton size="small" onClick={() => openEditDrawer(row)} sx={{ color: '#4ce1b6' }}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => openDeleteDialog(row)} sx={{ color: '#ef5350' }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      ),
    },
  ];

  const filterRowCells = columns.map((column) => {
    switch (column.field) {
      case 'name':
        return (
          <TextField
            key={column.field}
            placeholder="Search..."
            size="small"
            variant="standard"
            value={filters.search}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, search: e.target.value }));
              setPagination((prev) => ({ ...prev, page: 0 }));
            }}
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: '100%' }}
          />
        );
      case 'unit':
        return (
          <FormControl key={column.field} size="small" sx={{ width: '100%' }}>
            <Select
              value={filters.unit}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, unit: e.target.value }));
                setPagination((prev) => ({ ...prev, page: 0 }));
              }}
              displayEmpty
              variant="standard"
              disableUnderline
            >
              <MenuItem value="">All</MenuItem>
              {UNIT_OPTIONS.map((unit) => (
                <MenuItem key={unit} value={unit}>
                  {unit}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'status':
        return (
          <FormControl key={column.field} size="small" sx={{ width: '100%' }}>
            <Select
              value={filters.status}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, status: e.target.value }));
                setPagination((prev) => ({ ...prev, page: 0 }));
              }}
              displayEmpty
              variant="standard"
              disableUnderline
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        );
      case 'actions':
        return (
          <Box key={column.field} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            {(filters.search || filters.unit || filters.status) && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setFilters({ search: '', unit: '', status: '' });
                  setPagination((prev) => ({ ...prev, page: 0 }));
                }}
              >
                Clear
              </Button>
            )}
          </Box>
        );
      default:
        return <div key={column.field} />;
    }
  });

  return (
    <>
      <Paper elevation={0} sx={getStockListPaperSx(isDark)}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '12px',
                backgroundColor: alpha('#4ce1b6', 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CategoryOutlinedIcon sx={{ fontSize: 30, color: '#4ce1b6' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={getStockListHeaderTitleSx(isDark)}>
                Raw Material List
              </Typography>
              <Typography variant="body2" sx={getStockListHeaderSubtitleSx(isDark)}>
                Manage products / items used in inventory
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAddDrawer}
            sx={{
              backgroundColor: '#4ce1b6',
              color: '#000',
              '&:hover': {
                backgroundColor: '#3ab89a',
                color: '#000',
              },
            }}
          >
            Add Raw Material
          </Button>
        </Box>

        <DataTable
          columns={columns}
          data={paginatedData}
          loading={loading}
          pagination={pagination}
          onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
          onPerPageChange={(perPage) => setPagination((prev) => ({ ...prev, perPage, page: 0 }))}
          onSortChange={(newSort) => {
            setSortConfig(newSort);
            setPagination((prev) => ({ ...prev, page: 0 }));
          }}
          sortConfig={sortConfig}
          emptyMessage={
            rawMaterials.length === 0
              ? 'No raw materials found. Click "Add Raw Material" to create one.'
              : 'No raw materials match the selected filters.'
          }
          stickyHeader
          filterRow={filterRowCells}
        />
      </Paper>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={getDrawerRootSx()}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              borderBottom: `2px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
              backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CategoryOutlinedIcon sx={{ color: '#4ce1b6', fontSize: 22 }} />
              <Typography variant="h6" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 600 }}>
                {drawerMode === 'edit' ? 'Edit Raw Material' : drawerMode === 'view' ? 'View Raw Material' : 'Add Raw Material'}
              </Typography>
            </Box>
            <IconButton
              onClick={() => setDrawerOpen(false)}
              size="small"
              sx={{ color: isDark ? '#999' : '#666', p: 0.5 }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
              <TextField
                fullWidth
                label="Raw Material Name"
                required
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                disabled={isReadOnly}
              />
              <TextField
                select
                fullWidth
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
                disabled={isReadOnly}
                SelectProps={{ MenuProps: drawerSelectMenuProps }}
              >
                {['food', 'housekeeping', 'linen', 'amenities', 'maintenance', 'bar', 'kitchen', 'other'].map((c) => (
                  <MenuItem key={c} value={c}>{c.replace(/_/g, ' ')}</MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                label="SKU"
                value={formData.sku}
                onChange={(e) => setFormData((prev) => ({ ...prev, sku: e.target.value }))}
                disabled={isReadOnly}
              />
              <TextField
                select
                fullWidth
                label="Unit"
                required
                value={formData.unit}
                onChange={(e) => setFormData((prev) => ({ ...prev, unit: e.target.value }))}
                disabled={isReadOnly}
                SelectProps={{ MenuProps: drawerSelectMenuProps }}
              >
                {UNIT_OPTIONS.map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {unit}
                  </MenuItem>
                ))}
              </TextField>
              <StockPercentSlider
                label="Minimum stock level"
                value={formData.minimum_stock}
                onChange={(next) => setFormData((prev) => ({ ...prev, minimum_stock: next }))}
                disabled={isReadOnly}
                isDark={isDark}
                helperText="Alert when stock falls below this % of peak inventory."
              />
              <StockPercentSlider
                label="Reorder quantity"
                value={formData.reorder_quantity}
                onChange={(next) => setFormData((prev) => ({ ...prev, reorder_quantity: next }))}
                disabled={isReadOnly}
                isDark={isDark}
                helperText="Suggest reorder when stock drops below this % of peak inventory."
              />
              <Box>
                <Box sx={{ mb: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: isDark ? '#e5e7eb' : '#111827' }}
                  >
                    Status
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    flexWrap: 'wrap',
                    gap: 1.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary, fontSize: '0.875rem' }}
                  >
                    Inactive
                  </Typography>
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, is_active: e.target.checked }))
                    }
                    disabled={isReadOnly}
                    color="primary"
                    inputProps={{ 'aria-label': 'Raw material active status' }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary, fontSize: '0.875rem' }}
                  >
                    Active
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              p: 1.5,
              borderTop: `2px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
              backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              <Button
                onClick={() => setDrawerOpen(false)}
                variant="outlined"
                sx={{
                  borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                  color: isDark ? '#ddd' : '#666',
                  '&:hover': {
                    borderColor: '#4ce1b6',
                    backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                    color: '#4ce1b6',
                  },
                }}
              >
                {isReadOnly ? 'Close' : 'Cancel'}
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                disabled={isReadOnly || !isFormValid || saveLoading || formMeta.is_system_default}
                sx={{
                  backgroundColor: '#4ce1b6',
                  color: '#000',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#4ce1b6',
                    opacity: 0.9,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: isDark ? '#37474f' : '#cfd8dc',
                    color: isDark ? '#90a4ae' : '#78909c',
                  },
                }}
              >
                {saveLoading ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (deleteLoading) return;
          handleDeleteCancel();
        }}
        PaperProps={{
          sx: {
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: isDark ? '#fff' : '#212121',
            fontWeight: 600,
            pb: 2,
          }}
        >
          Delete raw material
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              color: isDark ? '#ddd' : '#666',
              fontSize: '1rem',
            }}
          >
            Are you sure you want to delete{' '}
            <strong>&quot;{rawMaterialToDelete?.name || '—'}&quot;</strong>?
            <br />
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            borderTop: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
          }}
        >
          <Button
            onClick={handleDeleteCancel}
            disabled={deleteLoading}
            sx={{
              color: isDark ? '#999' : '#757575',
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleteLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              '&:disabled': {
                backgroundColor: isDark ? '#555' : '#ccc',
              },
            }}
          >
            {deleteLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: '#fff' }} />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default StockRawMaterialSection;
